import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Cloud, Smartphone, Clock } from 'lucide-react';
import { format } from 'date-fns';

export interface SyncConflict {
  scriptId: string;
  scriptTitle: string;
  localVersion: {
    content: string;
    updatedAt: Date;
    wordCount: number;
  };
  serverVersion: {
    content: string;
    updatedAt: Date;
    wordCount: number;
  };
}

interface ConflictResolutionDialogProps {
  conflict: SyncConflict | null;
  onResolve: (choice: 'local' | 'server') => void;
  onCancel: () => void;
}

export const ConflictResolutionDialog = ({
  conflict,
  onResolve,
  onCancel,
}: ConflictResolutionDialogProps) => {
  const { t } = useTranslation();
  const [selectedVersion, setSelectedVersion] = useState<'local' | 'server' | null>(null);

  if (!conflict) return null;

  const truncateContent = (content: string, maxLength: number = 150) => {
    if (content.length <= maxLength) return content;
    return content.substring(0, maxLength) + '...';
  };

  return (
    <Dialog open={!!conflict} onOpenChange={() => onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {t('offline.conflict.title')}
          </DialogTitle>
          <DialogDescription>
            {t('offline.conflict.description', { title: conflict.scriptTitle })}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4 py-4">
          {/* Local Version */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              selectedVersion === 'local'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
            onClick={() => setSelectedVersion('local')}
          >
            <div className="flex items-center gap-2 mb-3">
              <Smartphone className="w-5 h-5 text-primary" />
              <span className="font-semibold">{t('offline.conflict.localVersion')}</span>
              <Badge variant="secondary" className="ml-auto">
                {t('offline.conflict.yourDevice')}
              </Badge>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>{format(new Date(conflict.localVersion.updatedAt), 'PPpp')}</span>
              </div>
              <div className="text-muted-foreground">
                {conflict.localVersion.wordCount} {t('editor.words').toLowerCase()}
              </div>
              <Separator />
              <p className="text-foreground/80 whitespace-pre-wrap">
                {truncateContent(conflict.localVersion.content)}
              </p>
            </div>
          </div>

          {/* Server Version */}
          <div
            className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
              selectedVersion === 'server'
                ? 'border-primary bg-primary/5'
                : 'border-border hover:border-primary/50'
            }`}
            onClick={() => setSelectedVersion('server')}
          >
            <div className="flex items-center gap-2 mb-3">
              <Cloud className="w-5 h-5 text-blue-500" />
              <span className="font-semibold">{t('offline.conflict.serverVersion')}</span>
              <Badge variant="secondary" className="ml-auto">
                {t('offline.conflict.cloud')}
              </Badge>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-1 text-muted-foreground">
                <Clock className="w-3 h-3" />
                <span>{format(new Date(conflict.serverVersion.updatedAt), 'PPpp')}</span>
              </div>
              <div className="text-muted-foreground">
                {conflict.serverVersion.wordCount} {t('editor.words').toLowerCase()}
              </div>
              <Separator />
              <p className="text-foreground/80 whitespace-pre-wrap">
                {truncateContent(conflict.serverVersion.content)}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onCancel}>
            {t('offline.conflict.skipForNow')}
          </Button>
          <Button
            onClick={() => selectedVersion && onResolve(selectedVersion)}
            disabled={!selectedVersion}
          >
            {t('offline.conflict.useSelected')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
